<template>
    <div class="columns is-centered " v-resize.initial="handleResize">
        <div id="confirmation-page" class="column is-10-desktop is-12-mobile is-12-tablet">
			<div v-if="useSmallLogo" class="simple-header">
				<header-logo></header-logo>
			</div>
            <h4 v-if="userAvailable" class="is-size-4">
				{{trackingText}}
				<a v-if="!loggedIn" href="/?login" class="store-navigation-color" @click.prevent="showModal('login')">
					create your account
				</a>
			</h4>
            <div v-if="userUnavailable">
                <p class="is-size-5">Please login to see your receipt</p>
                <a href="/?login" class="store-navigation-color" @click.prevent="showModal('login')">Show login screen</a>
            </div>
            <p v-if="msg" class="is-size-5">
                {{msg}}
            </p>
			<order-tracker-minimal />
            <div id = "order-info" class="columns">
                <div class="column is-7-desktop is-12-mobile is-12-tablet">
                    <div id="destination-description">
                        <div v-if="recentOrder && recentOrder.serviceType === EServiceType.SERVICE_TYPE_DELIVERY" class="padded-section">
                            <h5 tabindex="0" :aria-label="`deliver to ${displayFullAddress()}`" class="is-size-5-5 is-bold">Delivery Address</h5>
                            {{displayFullAddress()}}
                        </div>

                        <div class="padded-section" v-if="orderMap.origin && recentOrder">
                            <h5 tabindex="0" :aria-label="`order at store: ${orderMap.origin.name}`" class="is-size-5-5 is-bold">Store</h5>
                            {{orderMap.origin.name}}<br/>
                            {{orderMap.origin.address}}
                            <p v-if="orderMap.origin && storePhone">
                                <a :aria-label="`if you would like to contact the store call: ${storePhone}`" :href="`tel:${orderMap.origin.contactPhone}`">{{storePhone}}</a>
                            </p>
                        </div>
                    </div>
                    <div v-if="orderMap.origin !== null && $mq === 'mobile' && appLoaded">
                        <BaseMap
                            :zoom="12"
                            :storeLocation="orderMap.origin"
                            :directions="( (recentOrder.serviceType === EServiceType.SERVICE_TYPE_DELIVERY)
                                             && orderMap.destination && orderMap.origin) ? orderMap : null"
                            height="550px;"
                        />
                    </div>

                    <p class="is-bold" v-if="recentOrder"><span class="is-size-5">Your Order: #{{getOrderId}}</span>
                        <span @click="clickEmailReceipt" class="is-pulled-right store-navigation-color is-clickable">Email receipt</span>
                    </p>
                    <br>
                    <template v-if="recentOrder">
                        <checkout-item
                            v-for="(item, index) in recentOrder.items"
                            :key="index"
                            :index="index"
                            :isMinified="true"
                            :isRecentOrder="true"
                            :item="item"
                            >
                        </checkout-item>
                        <p v-if="recentOrder.orderNotes">
                            Instructions: <i>{{recentOrder.orderNotes}}</i>
                        </p>

                        <div tabindex="0" class="checkout-total is-size-6">
                            <p :key="key" v-for="(value, key) in streamLinedTotals()">
                                <span class="is-bold">{{startCase(key)}}</span> <span class="is-pulled-right has-text-grey">{{ centsToDollars(value) }}</span>
                            </p>
                        </div>
                    </template>

                </div>
                <div class="column"></div>
                <div class="column is-5-desktop is-12-mobile is-12-tablet">
                    <div v-if="orderMap.origin !== null && $mq !== 'mobile' && appLoaded">
                        <BaseMap
                            :zoom=12
                            :storeLocation="orderMap.origin"
                            :directions="( (recentOrder.serviceType === EServiceType.SERVICE_TYPE_DELIVERY)
                                             && orderMap.destination && orderMap.origin) ? orderMap : null"
                            height="550px;"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import resize from 'vue-resize-directive';
import EventBus from '@EventBus';
import { AsYouType } from 'libphonenumber-js';
import { CART, ACCOUNT, ACCOUNT_CHECKOUT, LOCATION, MENU_ITEMS, RECENT_ORDERS, APP_STORE } from '@/stores/defs';
import { EServiceType } from '@/models/interfaces/Orders/EServiceType';
import { mapGetters, mapActions } from 'vuex';
import { printCompactedOptions, getParam, extractOrderId, generateFullAddress } from '@/utils/helper.functions';
import { service } from '@/services/app.service';
import { startCase } from 'lodash';
import AddressModel from '@/models/AddressModel';
import CheckoutItem from '@/components/checkout/CheckoutItem';
import DateManipulator from '@/utils/DateManipulator';
import EmailReceipt from '@/components/checkout/EmailReceipt.vue';
import Formatter from '@/utils/Formatter';
import LocationModel from '@/models/LocationModel';
import locationService from '@/services/location.service';
import orderService from '@/services/order.service';
import OrderTracker from '@/components/orders/OrderTracker';
import HeaderLogo from '@NavItem/HeaderLogo.vue';
import recentOrderService from '@/services/recent.orders.service';
import Login from '@/components/auth/Login';
import { ERROR_MSG } from '@/common/messages/error.msg';
import modalMixin from '@Mixin/modalMixin';
import orderMixin from '@Mixin/orderMixin';
import headerMixin from '@Mixin/headerMixin';
import OrderTrackerMinimal from '@OrderConfirmation/OrderTrackerMinimal';

export default {
	mixins: [
		modalMixin,
		orderMixin,
		headerMixin,
	],
	props: {
		checkoutOrder: {
			type: Object,
			default: null,
		},
		fromCheckout: {
			type: Boolean,
			default: true,
		},
	},
	directives: {
		resize,
	},
	watch: {
		allMenuItems() {
			if (!this.pageReady) {
				this.populateOrder();
			}
		},
		loggedIn(isLoggedIn) {
			if (isLoggedIn && !this.pageReady) {
				this.populateOrder();
			}
		},
	},
	components: {
		'order-tracker': OrderTracker,
		'header-logo': HeaderLogo,
		'checkout-item': CheckoutItem,
		'order-tracker-minimal': OrderTrackerMinimal,
	},
	computed: {
		...mapGetters({
			items: CART.ITEMS,
			priceTotal: CART.PRICE,
			isGuest: ACCOUNT_CHECKOUT.GET_IS_GUEST,
			guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
			address: ACCOUNT_CHECKOUT.GET_DELIVERY_ADDRESS,
			serviceType: ACCOUNT_CHECKOUT.GET_SERVICE_TYPE,
			orderTime: ACCOUNT_CHECKOUT.GET_ORDER_TIME,
			allMenuItems: MENU_ITEMS.GET_ALL,
			appLoaded: APP_STORE.LOADED,
			loggedIn: ACCOUNT.GET_LOGGED_IN,
		}),
		storePhone() {
			if (this.orderMap.origin.contactPhone) {
				return new AsYouType('US').input(this.orderMap.origin.contactPhone);
			}
			return null;
		},
		promiseTime() {
			if (this.recentOrder) {
				const promiseTime = moment(this.recentOrder.promiseDate);

				if (this.recentOrder.pickupDelay) {
					promiseTime.add(this.recentOrder.pickupDelay, 'seconds');
				}
				let dateFormat = 'LLL dd, yyyy: hh:mma';
				if (DateManipulator.isMomentToday(moment(this.recentOrder.promiseDate))) {
					dateFormat = 'hh:mma';
					return `Today: ${promiseTime.format(dateFormat)} - ${promiseTime
						.add({ minutes: 15 })
						.toFormat(dateFormat)}`;
				}
				return `${promiseTime.format(dateFormat)} - ${promiseTime.add({ minutes: 15 }).toFormat('hh:mma')}`;
			}
		},
		getOrderId() {
			return extractOrderId(this.orderId);
		},
		trackingText() {
			const isLoggedIn = this.loggedIn;
			if (!isLoggedIn) {
				return 'Sign in to track your next order from any device:';
			}
			return 'Track your order from any device!';
		},
		userAvailable() {
			const isLoggedIn = this.loggedIn;
			const isGuest = this.isGuest;
			const hasMessage = Boolean(this.msg);
			if (hasMessage) {
				return false;
			}
			if (isLoggedIn || isGuest) {
				return true;
			}
		},
		userUnavailable() {
			const unavailable = !this.userAvailable;
			const appLoaded = this.appLoaded;
			return (unavailable && appLoaded);
		},
		useSmallLogo() {
			const screen = this.$mq;
			const hasSideBar = this.$layouts.hasSideBar(screen);
			return hasSideBar;
		},
	},
	data() {
		return {
			AsYouType,
			trackerLocation: null,
			EServiceType,
			msg: '',
			lb: getParam('lb'),
			orderId: getParam('order_id'),
			recentOrder: null,
			editingOrder: false,
			orderMap: {
				destination: null,
				origin: null,
			},
			pageReady: false,
			receiptLoader: false,
		};
	},
	mounted() {
		//  if we've loaded all menu items, and recent order hasnt been populated yet
		if (this.allMenuItems && !this.pageReady) {
			this.populateOrder();
		}
		this.setupNavigationHeader();
	},
	methods: {
		handleResize() {
			EventBus.$emit('recalculate-header');
		},
		setupNavigationHeader() {
			// mobile only
			if (!this.$isMobile) {
				return;
			}
			this._setHeaderItems([
				'empty',
				'logo',
				'empty',
			]);
		},
		startCase,
		streamLinedTotals() {
			const streamLinedTotal = {};
			for (const key in this.recentOrder.totals) {
				if (!(this.recentOrder.totals[key] === 0 && !['subTotal', 'total'].includes(key)) && key !== 'collected') {
					// values to be subtracted from total
					if (['refund', 'discount'].includes(key)) {
						streamLinedTotal[key] = this.recentOrder.totals[key] * -1;
					} else {
						streamLinedTotal[key] = this.recentOrder.totals[key];
					}
				}
			}
			return streamLinedTotal;
		},
		...mapActions({
			clearCart: CART.CLEAR_CART,
		}),
		clickEmailReceipt() {
			this.showModal('emailReceipt');
		},
		printCompactedOptions,
		centsToDollars: Formatter.centsToDollars,
		showModal(modalType) {
			let title,
				component,
				contentProps = {},
				modalName = 'contentModal';
			const skipPhoneEntry = this.userAvailable && !this.loggedIn;
			if (modalType === 'emailReceipt') {
				title = 'A Copy for your Records';
				component = EmailReceipt;
				contentProps = {
					orderId: this.orderId,
					lb: this.lb,
					receiptType: 'email',
				};
			} else if (modalType === 'login') {
				title = 'Login';
				component =  Login;
				modalName = 'loginModal';
				contentProps = {
					routeTo: this.$route.fullPath,
					skipToPin: skipPhoneEntry,
				};
			}
			this.triggerModal({
				component,
				title,
				modalName,
				classes: 'global-modal',
				contentProperties: contentProps,
			});
		},
		populateOrder() {
			if (this.userUnavailable) {
				return;
			}
			if (!this.recentOrder && !this.editingOrder && !this.checkoutOrder) {
				this.editingOrder = true;
				const servicePromise = this.getOrderFromService();
				servicePromise.then(res => {
					if (res.data.body.errors) {
						this.msg = ERROR_MSG.ORDER.MISC.CUSTOMER_MISMATCH;
					} else {
						const convertedOrder = recentOrderService.convertSingleOrder(res.data.body);
						this.recentOrder = convertedOrder;
						this.generateMapAddress(this.recentOrder.locationBase);
						this.editingOrder = false;
						this.pageReady = true;
					}
				}).catch(err => {
					console.log(err);
				});
			}
			if (this.checkoutOrder) {
				// no need to call api
				const convertedOrder = recentOrderService.convertSingleOrder(this.checkoutOrder);
				this.recentOrder = convertedOrder;
				this.pageReady = true;
				this.editingOrder = false;
				this.generateMapAddress(this.recentOrder.locationBase);
			}
		},
		generateMapAddress(base) {
			locationService
				.getSingleLocation(base)
				.then(res => {
					if (res) {
						if (res instanceof (LocationModel)) {
							res['hydrationStrategy'] = 'hydrateFromStore';
						}
						const storeLocation = new LocationModel(res);
						this.orderMap.origin = storeLocation;
						if (this.recentOrder.serviceType === EServiceType.SERVICE_TYPE_DELIVERY) {
							this.orderMap.destination = this.recentOrder.deliveryDetails.address;
						}
					}
				})
				.catch(err => {
					console.log(err);
				});
		},
		displayFullAddress() {
			let address = this.recentOrder.deliveryDetails.address.formattedAddress;
			const aptNum = this.recentOrder.deliveryDetails.address.aptNum;
			address = generateFullAddress(address, aptNum);
			return address;
		},
	},
};
</script>

<style lang="scss" scoped>
@import 'globalStyles';

#destination-description {
    margin-bottom: 1em;
}

#order-progress {
    margin: 0 auto;
    width: 80%;
    margin-bottom: 4em;
    position: relative;
}

.address {
    margin-bottom: 3em;
}

#confirmation-page {
	margin-top: 2em;
	height: 100%;
	.checkout-total {
		margin-top: 2em;
	}
}

#confirmation-page h4 {
	padding-bottom: 1em;
}

#confirmation-page #header-txt {
    margin-bottom: 4em;
}
#confirmation-page #header-txt p {
    margin-bottom: 1em;
}

#order-summary {
    margin-bottom: 1em;
}

#order-info {
    margin-top: 3em;

    min-height: auto;
}

.receipt-wrapper {
    width: 40%;
    margin: 0 auto;
    height: 100%;
}

.padded-section {
    width: 100%;
    border-bottom: 2px solid whitesmoke;
    padding: 1em 0em;
}

.receipt {
    min-height: 75vh;
    box-shadow: 0 9px 0px 0px white, 0 -9px 0px 0px white, 2px 5px 15px -4px rgba(0, 0, 0, 0.5),
        -12px 0 25px -15px rgba(0, 0, 0, 0.5);
    letter-spacing: 1px;
    position: relative;
    text-align: center;
    transition: all 0.25s ease-in-out;
    background-color: #f8f8f8;
    background-image:
            /* Top jagged */ linear-gradient(
            145deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 1) 50%,
            rgba(255, 255, 255, 0) 50%,
            rgba(255, 255, 255, 0) 100%
        ),
        linear-gradient(
            -145deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 1) 50%,
            rgba(255, 255, 255, 0) 50%,
            rgba(255, 255, 255, 0) 100%
        ),
        /* Bottom jagged */
            linear-gradient(
                45deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 50%,
                rgba(255, 255, 255, 0) 50%,
                rgba(255, 255, 255, 0) 100%
            ),
        linear-gradient(
            -45deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 1) 50%,
            rgba(255, 255, 255, 0) 50%,
            rgba(255, 255, 255, 0) 100%
        ),
        /* Top and bottom fade  */ linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 100%),
        linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%);
    background-position:
            /* Top jagged */ top center, top center, /* Bottom jagged */ bottom center,
        bottom center, /* Top fade */ top center, /* Bottom fade */ bottom center;
    background-size:
            /* Top + bottom jagged */ 0.75rem 0.75rem, 0.75rem 0.75rem, 0.75rem 0.75rem,
        0.75rem 0.75rem, /* Top + bottom fade */ 100% 1rem, 100% 0rem;
    background-repeat: repeat-x;
    font-family: Montserrat, Helvetica Neue, Helvetica, Arial, sans-serif;
    letter-spacing: 1px;
    padding: 4em 0;
    position: relative;
    text-align: center;
    transition: all 0.25s ease-in-out;
}

.simple-header {
	display: flex;
	justify-content: center;
	width: 100%;
	height: $navigationBarHeight;
	img {
		height: 100%;
		cursor: pointer;
	}
}

@include desktop {
	#confirmation-page {
		margin-top: $navigationBarHeight;
		padding-bottom: 3em;
	}
}

@include mobile {
    .receipt-wrapper {
        width: 80%;
        margin: 0 auto;
    }

    #confirmation-page {
        margin-left: 0em;
		margin-top: 0em;

		-webkit-overflow-scroll: touch;
        #order-tracker {
            .order-promise-time {
                top: 0;
            }
        }
    }

    #order-info {
        margin-top: 0em;
    }
}
</style>




// WEBPACK FOOTER //
// src/components/checkout/OrderConfirmation.vue